import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import DOMPurify from "dompurify";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { Box, CircularProgress, Modal, IconButton } from "@mui/material";

import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import WYSIWYGEditor from "components/WYSIWYGEditor/WYSIWYGEditor";
import MDInput from "components/MDInput";
import CloseIcon from "@mui/icons-material/Close";

import { getFAQById, resetFAQDetails } from "store/reducers/faqsSlice";

const AddFAQ = ({
  openModal,
  onCloseModal,
  isEdit,
  onCreateFAQ,
  onUpdateFAQ,
  selectedFAQ,
}) => {
  const dispatch = useDispatch();
  const { faqDetails } = useSelector((state) => state.faqs);

  const [isLoading, setLoading] = useState(false);
  const [isFetchingPolicy, setFetchingPolicy] = useState(false);
  const [disableSaveBtn, setdisableSaveBtn] = useState(true);
  const [disabledQuestionBtn, setDisabledQuestionBtn] = useState(true);

  const faqSchema = yup.object({
    question: yup
      .string()
      .trim()
      .required(`FAQ Question is required`)
      .test(
        "test-question",
        "Only numeric values are not allowed.",
        (value, ctx) => {
          if (value.match(/^\d+$/)) {
            return ctx.createError({
              path: "question",
              message: "Only numeric values are not allowed.",
            });
          }
          return true;
        }
      ),
    content: yup
      .string()
      .trim()
      .required(`FAQ content is required`)
      .test("test-content", "FAQ Content is required", (value, ctx) => {
        if (value === "<p></p>" || value === "<p>&nbsp;</p>") {
          return ctx.createError({
            path: "content",
            message: "FAQ Content is required",
          });
        }
        return true;
      }),
  });

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isValid, isSubmitting, dirtyFields },
    reset,
  } = useForm({
    mode: "all",
    resolver: yupResolver(faqSchema),
  });

  async function fetchFAQ() {
    try {
      setFetchingPolicy(true);
      await dispatch(getFAQById(selectedFAQ?.id));
    } catch (error) {
      console.log({ error });
      onCloseModal();
    } finally {
      setFetchingPolicy(false);
    }
  }

  useEffect(() => {
    if (isEdit && selectedFAQ?.id) fetchFAQ();
  }, [isEdit, selectedFAQ]);

  useEffect(() => {
    if (faqDetails) {
      reset(
        {
          question: faqDetails?.question,
          content: faqDetails?.content,
        },
        {
          keepIsValid: false,
          keepErrors: true,
          keepDirty: false,
        }
      );
    }
  }, [faqDetails]);

  useEffect(() => {
    return () => {
      dispatch(resetFAQDetails());
    };
  }, []);

  const onAddFAQSubmit = (data) => {
    try {
      setLoading(true);
      const cleanHtml = DOMPurify.sanitize(data?.content);
      onCreateFAQ({ question: data?.question, content: cleanHtml });
    } catch (error) {
      console.log({ error });
      toast.error("Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  const onUpdateFAQSubmit = (data) => {
    try {
      setLoading(true);
      const cleanHtml = DOMPurify.sanitize(data?.content);
      onUpdateFAQ({
        faqId: selectedFAQ?.id?.toString(),
        question: data?.question,
        content: cleanHtml,
        isActive: true,
      });
    } catch (error) {
      console.log({ error });
      toast.error("Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (data) => {
    if (`${selectedFAQ.content}` === `${data}`.trim()) {
      setdisableSaveBtn(true);
    } else {
      setdisableSaveBtn(false);
    }
  };

  const handleQuestionEdit = (data) => {
    if (selectedFAQ.question === data.target.value) {
      setDisabledQuestionBtn(true);
    } else {
      setDisabledQuestionBtn(false);
    }
  };
  return (
    <Modal
      open={openModal}
      onClose={(_, reason) => {
        if (reason === "backdropClick") return;
        onCloseModal();
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      disableEscapeKeyDown>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          boxShadow: 24,
          maxHeight: "calc(100vh - 100px)",
          width: "700px",
          maxWidth: "calc(100vw - 48px)",
          overflow: "auto",
          p: 4,
          borderRadius: 3,
        }}>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center">
          <MDTypography id="modal-modal-title" variant="h4" component="h2">
            {isEdit ? "Edit FAQ" : "Add FAQ"}
          </MDTypography>
          <IconButton onClick={onCloseModal}>
            <CloseIcon fontSize="large" />
          </IconButton>
        </MDBox>
        {isFetchingPolicy ? (
          <Box
            sx={{
              textAlign: "center",
              paddingTop: "32px",
              paddingBottom: "24px",
              height: "800px",
              display: "grid",
              placeItems: "center",
              maxHeight: "calc(100vh - 100px)",
            }}>
            <CircularProgress color="info" />
          </Box>
        ) : (
          <>
            <MDBox marginTop="32px">
              <MDTypography variant="h5" mb={2}>
                Question
              </MDTypography>
              <MDInput
                {...register("question")}
                fullWidth
                error={!!errors?.question}
                autoFocus
                onChange={handleQuestionEdit}
                helperText={
                  errors?.question && (
                    <MDTypography color="error" variant="caption">
                      {errors?.question?.message}
                    </MDTypography>
                  )
                }></MDInput>
            </MDBox>
            <MDBox marginTop="32px">
              <MDTypography variant="h5" mb={2}>
                Content
              </MDTypography>
              <Controller
                name="content"
                control={control}
                render={({ field: { onChange, value } }) =>
                  !isEdit ? (
                    <WYSIWYGEditor value={value} onChange={onChange} />
                  ) : (
                    <WYSIWYGEditor
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                        handleEdit(e);
                      }}
                    />
                  )
                }
              />
              {errors?.content && (
                <MDTypography color="error" variant="caption">
                  {errors?.content?.message}
                </MDTypography>
              )}
            </MDBox>
            <MDBox align="right" marginTop="32px">
              <MDButton onClick={onCloseModal}>Discard</MDButton>
              {isEdit ? (
                <>
                  <MDButton
                    onClick={handleSubmit(onUpdateFAQSubmit)}
                    color="info"
                    disabled={
                      Object.keys(errors).length > 0 ||
                      !isValid ||
                      isSubmitting ||
                      (disableSaveBtn && disabledQuestionBtn)
                    }>
                    Save
                  </MDButton>
                </>
              ) : (
                <MDButton
                  onClick={handleSubmit(onAddFAQSubmit)}
                  color="info"
                  disabled={
                    Object.keys(errors).length > 0 || !isValid || isSubmitting
                  }
                  isLoading={isLoading}>
                  Create
                </MDButton>
              )}
            </MDBox>
          </>
        )}
      </Box>
    </Modal>
  );
};

AddFAQ.propTypes = {
  openModal: PropTypes.bool,
  onCloseModal: PropTypes.func,
  isEdit: PropTypes.bool,
  onCreateFAQ: PropTypes.func,
  onUpdateFAQ: PropTypes.func,
  selectedFAQ: PropTypes.object,
};

AddFAQ.defaultProps = {
  openModal: false,
  onCloseModal: () => {},
  isEdit: false,
  onCreateFAQ: () => {},
  onUpdateFAQ: () => {},
  selectedFAQ: null,
};

export default AddFAQ;
